import { Component, OnInit, Input, OnChanges, SimpleChanges , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { switchMap } from 'rxjs';
import { NextWeekPlanTaskService, NextWeekPlanTask, CreateNextWeekPlanTaskDto } from '../../services/next-week-plan-task.service';
import { WeekPlanTaskService, CreateWeekPlanTaskDto } from '../../services/week-plan-task.service';

@Component({
  selector: 'app-next-week-plan',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Tag,
    Tooltip,
    Dialog,
    InputText,
    Select,
    DatePicker,
    ConfirmDialog,
  ],
  templateUrl: './next-week-plan.component.html',
  styleUrl: './next-week-plan.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class NextWeekPlanComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  @Input() projectId: string = '';

  tasks: NextWeekPlanTask[] = [];
  selectedTask: NextWeekPlanTask | null = null;
  displayTaskDialog: boolean = false;
  isEditTaskMode: boolean = false;
  taskStartDate: Date | null = null;
  taskTargetDate: Date | null = null;
  loading: boolean = false;
  fieldTouched: Record<string, boolean> = {};

  taskStatusOptions = [
    { label: 'On Track', value: 'On Track' },
    { label: 'On Hold', value: 'On Hold' },
    { label: 'Delayed', value: 'Delayed' },
    { label: 'Completed', value: 'Completed' }
  ];

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private nextWeekPlanTaskService: NextWeekPlanTaskService,
    private weekPlanTaskService: WeekPlanTaskService
  ) {}

  ngOnInit() {
    if (this.projectId) {
      this.loadTasks();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId'] && this.projectId) {
      this.loadTasks();
    }
  }

  loadTasks() {
    if (!this.projectId) {
      return;
    }
    this.loading = true;
    this.nextWeekPlanTaskService.getByProjectId(this.projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (tasks) => {
        this.tasks = tasks;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading tasks:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load tasks. Please try again.'
        });
        this.loading = false;
      }
    });
  }

  showAddTaskDialog() {
    if (!this.projectId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Project ID is required to add tasks.'
      });
      return;
    }
    const todayStr = this.formatDateToLocalString(new Date());
    this.selectedTask = {
      id: '',
      projectId: this.projectId,
      taskNumber: this.generateTaskNumber(),
      task: '',
      startDate: todayStr,
      targetDate: '',
      status: 'On Track',
      comments: ''
    };
    this.taskStartDate = new Date();
    this.taskTargetDate = null;
    this.fieldTouched = {};
    this.isEditTaskMode = false;
    this.displayTaskDialog = true;
  }

  showEditTaskDialog(task: NextWeekPlanTask) {
    this.selectedTask = { ...task };
    this.taskStartDate = task.startDate ? this.parseLocalDateString(task.startDate) : null;
    this.taskTargetDate = task.targetDate ? this.parseLocalDateString(task.targetDate) : null;
    this.fieldTouched = {};
    this.isEditTaskMode = true;
    this.displayTaskDialog = true;
  }

  resetTaskForm() {
    this.fieldTouched = {};
    if (!this.selectedTask) return;
    if (this.isEditTaskMode) {
      const task = this.tasks.find(t => t.id === this.selectedTask?.id);
      if (task) {
        this.selectedTask = { ...task };
        this.taskStartDate = task.startDate ? this.parseLocalDateString(task.startDate) : null;
        this.taskTargetDate = task.targetDate ? this.parseLocalDateString(task.targetDate) : null;
      }
    } else {
      this.selectedTask.taskNumber = this.generateTaskNumber();
      this.selectedTask.task = '';
      this.selectedTask.startDate = '';
      this.selectedTask.targetDate = undefined;
      this.selectedTask.status = 'To Start';
      this.selectedTask.comments = '';
      this.taskStartDate = null;
      this.taskTargetDate = null;
    }
  }

  saveTask() {
    this.fieldTouched['task'] = true;

    if (!this.selectedTask || !this.selectedTask.task.trim()) {
      return;
    }

    if (!this.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Project ID is required.'
      });
      return;
    }

    if (this.taskStartDate) {
      this.selectedTask.startDate = this.formatDateToLocalString(this.taskStartDate);
    }

    if (this.taskTargetDate) {
      this.selectedTask.targetDate = this.formatDateToLocalString(this.taskTargetDate);
    } else {
      this.selectedTask.targetDate = undefined;
    }

    if (this.isEditTaskMode) {
      // Update existing task
      this.nextWeekPlanTaskService.updateTask(this.selectedTask.id, this.selectedTask).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updatedTask) => {
          const index = this.tasks.findIndex(t => t.id === updatedTask.id);
          if (index !== -1) {
            this.tasks[index] = updatedTask;
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Task updated successfully.'
          });
          this.displayTaskDialog = false;
          this.selectedTask = null;
        },
        error: (error) => {
          console.error('Error updating task:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update task. Please try again.'
          });
        }
      });
    } else {
      // Create new task
      const createDto: CreateNextWeekPlanTaskDto = {
        projectId: this.projectId,
        taskNumber: this.selectedTask.taskNumber,
        task: this.selectedTask.task,
        startDate: this.selectedTask.startDate,
        targetDate: this.selectedTask.targetDate,
        status: this.selectedTask.status,
        comments: this.selectedTask.comments
      };

      this.nextWeekPlanTaskService.addTask(createDto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (newTask) => {
          this.tasks.push(newTask);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Task added successfully.'
          });
          this.displayTaskDialog = false;
          this.selectedTask = null;
        },
        error: (error) => {
          console.error('Error adding task:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add task. Please try again.'
          });
        }
      });
    }
  }

  /** Move task from Next Week Plan to Current Week Plan: create in current, then delete from next. */
  moveToCurrentWeek(task: NextWeekPlanTask) {
    if (!this.projectId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Project ID is required.'
      });
      return;
    }
    const createDto: CreateWeekPlanTaskDto = {
      projectId: this.projectId,
      taskNumber: task.taskNumber,
      task: task.task,
      startDate: task.startDate,
      targetDate: task.targetDate,
      status: task.status,
      comments: task.comments
    };
    this.weekPlanTaskService.addTask(createDto).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(() => this.nextWeekPlanTaskService.deleteTask(task.id))
    ).subscribe({
      next: () => {
        const index = this.tasks.findIndex(t => t.id === task.id);
        if (index !== -1) {
          this.tasks.splice(index, 1);
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Moved',
          detail: 'Task moved to Current Week Plan.'
        });
      },
      error: (error) => {
        console.error('Error moving task to current week:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Failed to move task. Please try again.'
        });
      }
    });
  }

  deleteTask(task: NextWeekPlanTask) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${task.task || ''}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.nextWeekPlanTaskService.deleteTask(task.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            const index = this.tasks.findIndex(t => t.id === task.id);
            if (index !== -1) {
              this.tasks.splice(index, 1);
            }
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Task deleted successfully.'
            });
          },
          error: (error) => {
            console.error('Error deleting task:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete task. Please try again.'
            });
          }
        });
      }
    });
  }

  generateTaskNumber(): string {
    if (this.tasks.length === 0) {
      return '00001';
    }
    const maxId = Math.max(...this.tasks.map(t => {
      const num = parseInt(t.taskNumber.replace(/\D/g, ''));
      return isNaN(num) ? 0 : num;
    }));
    return String(maxId + 1).padStart(5, '0');
  }

  /** Parse YYYY-MM-DD as local date (avoids timezone off-by-one). */
  private parseLocalDateString(dateStr: string): Date | null {
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    const date = new Date(y, m, d);
    if (isNaN(date.getTime())) return null;
    return date;
  }

  /** Format Date to YYYY-MM-DD using local date. */
  private formatDateToLocalString(date: Date): string {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  formatTaskDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = this.parseLocalDateString(dateString);
    if (!date) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  getTaskStatusSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'On Track':
      case 'Completed':
        return 'success';
      case 'On Hold':
        return 'warn';
      case 'Delayed':
        return 'danger';
      default:
        return 'secondary';
    }
  }
}

